/**
 * CMS Header Query
 *
 * Server-side query for fetching header configuration by tenant.
 */

import { query } from "@solidjs/router";
import type { CMSHeader, CMSHeaderResponse } from "~/types/cms";
import { env } from "~/config/env";
import { buildHeadersListUrl } from "../queryUrls";
import { logger } from "~/lib/logger/logger";
import { fetchFromCMS } from "../queryUtils";
import { evictIfFull, touchEntry } from "~/lib/cache/cappedMap";

const LOG_PREFIX = "[headerByTenant]";

/** 60s cache — header navigation rarely changes */
const CACHE_TTL_MS = 60_000;
const MAX_CACHE_SIZE = 20;
const headerCache = new Map<string, { data: CMSHeader | null; expiresAt: number }>();

/** Clear the server-side header Map cache. Called by webhook invalidation. */
export function clearHeaderCache(tenantSlug?: string) {
  if (tenantSlug) {
    headerCache.delete(tenantSlug);
  } else {
    headerCache.clear();
  }
}

/**
 * Server-side query for fetching header by tenant
 *
 * @param tenantSlug - The tenant slug (e.g., 'absolut-radio')
 * @returns Header configuration with navigation data, or null if not found
 */
export const getHeaderByTenantQuery = query(async (tenantSlug: string) => {
  "use server";

  const cached = headerCache.get(tenantSlug);
  if (cached && Date.now() < cached.expiresAt) {
    touchEntry(headerCache, tenantSlug);
    return cached.data;
  }

  // Header is scoped server-side to this tenant's `usage: 'website'` entry.
  const url = `${env.cmsApiUrl}${buildHeadersListUrl(tenantSlug, "website")}`;

  const data = await fetchFromCMS<CMSHeaderResponse>(url, { logPrefix: LOG_PREFIX });

  if (!data?.docs || !Array.isArray(data.docs)) {
    logger.warn(`${LOG_PREFIX} Invalid response structure`);
    return null;
  }

  const header = data.docs[0];

  if (!header) {
    logger.warn(`${LOG_PREFIX} No header found for tenant: ${tenantSlug}`);
    return null;
  }

  logger.info(`${LOG_PREFIX} Found header: ${header.name}`);
  evictIfFull(headerCache, MAX_CACHE_SIZE);
  headerCache.set(tenantSlug, { data: header, expiresAt: Date.now() + CACHE_TTL_MS });
  return header;
}, "headerByTenant");
